import React, { useEffect, useRef, useState } from 'react';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H1 } from '@decisiv/ui-components/lib/components/Typography';

import { createSortHandler } from 'components/pages/Users/utils/sortUtils';

import CreateUserButton from './CreateUserButton';
import CreateUserWizard from './CreateUserWizard';
import UsersFilters from './UsersFilters';
import UsersSearch from './UsersSearch';
import UsersTable from './UsersTable';
import { PageBody, PageHeader } from './styles';
import { PageSurface } from '../../shared/styles';
import useFilterOptions from '../hooks/useFilterOptions';
import useUsers from '../hooks/useUsers';
import { readUrlState, writeUrlState } from '../urlState';

/**
 * Everything below the Management shell on /management/users.
 *
 * All of the page's state lives here rather than in Users.jsx, and that split is
 * load-bearing: KeyDS LeftNav creates styled components inside a useMemo, so its
 * hook count changes between renders and re-rendering it throws "Rendered fewer
 * hooks than expected" on the React 16 portal pins (same root cause as the
 * defaultNavExpandedState note in ManagementLayout). Keeping the state here means
 * a fetch only re-renders this subtree, never the nav.
 */
function UsersContent() {
  const initial = useRef(readUrlState()).current;

  const [page, setPage] = useState(initial.page);
  const [sortBy, setSortBy] = useState(initial.sortBy);
  const [sortOrder, setSortOrder] = useState(initial.sortOrder);
  const [filters, setFilters] = useState(initial.filters);
  const [query, setQuery] = useState(initial.query);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  // Bumped when the wizard creates someone, which is what pulls them into the
  // list — the effect below refetches on it. It also re-asks for the account
  // filter options, since a user created on an account that had none makes that
  // account an option for the first time.
  const [createdCount, setCreatedCount] = useState(0);

  const { users, isLoading, error, pagination, fetchUsersData } = useUsers();
  const {
    accountOptions,
    jobRoleOptions,
    permissionOptions,
    searchAccounts,
  } = useFilterOptions(createdCount);

  const onSort = createSortHandler((column) => {
    setSortBy(column);
    setPage(1);
  }, setSortOrder);

  // Narrowing the results can leave the current page past the end, so both
  // filtering and searching return to the first page.
  const onFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  const onSearch = (term) => {
    setQuery(term);
    setPage(1);
  };

  // Which of the two empty states to show turns on this.
  const isNarrowed =
    query.length > 0 ||
    Object.values(filters).some((values) => values.length > 0);

  useEffect(() => {
    fetchUsersData({ filters, page, query, sortBy, sortOrder });
  }, [createdCount, fetchUsersData, filters, page, query, sortBy, sortOrder]);

  // The address bar tracks page, sort, filters and the search term so a reload
  // lands on the same view.
  useEffect(() => {
    writeUrlState({ filters, page, query, sortBy, sortOrder });
  }, [filters, page, query, sortBy, sortOrder]);

  return (
    <PageSurface>
      <PageHeader>
        <Flex alignItems="flex-start" justifyContent="space-between">
          <Flex flexDirection="column">
            <H1 style={{ margin: 0 }}>Users</H1>
            <Flex marginTop={1}>
              <CreateUserButton onCreateUser={() => setIsWizardOpen(true)} />
            </Flex>
          </Flex>
          <UsersSearch value={query} onSearch={onSearch} />
        </Flex>
      </PageHeader>
      <UsersFilters
        accountOptions={accountOptions}
        filters={filters}
        jobRoleOptions={jobRoleOptions}
        permissionOptions={permissionOptions}
        onChange={onFiltersChange}
        onSearchAccounts={searchAccounts}
      />
      <PageBody>
        <UsersTable
          error={error}
          isLoading={isLoading}
          isNarrowed={isNarrowed}
          onPageChange={setPage}
          onSort={onSort}
          pagination={pagination}
          query={query}
          sortBy={sortBy}
          sortOrder={sortOrder}
          users={users}
        />
      </PageBody>
      <CreateUserWizard
        visible={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onCreated={() => setCreatedCount((count) => count + 1)}
      />
    </PageSurface>
  );
}

export default UsersContent;
